const TOKEN_KEY = 'landing_admin_token';

export function apiOrigin(): string {
  const api = import.meta.env.VITE_API_ORIGIN?.trim();
  if (api) return api.replace(/\/$/, '');
  return '';
}

export function landingApiBase(): string | null {
  const origin = apiOrigin();
  return origin ? `${origin}/api/public/landing` : null;
}

export function getAdminToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAdminToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function adminLogin(password: string): Promise<string> {
  const base = landingApiBase();
  if (!base) throw new Error('API no configurada (VITE_API_ORIGIN).');
  const res = await fetch(`${base}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ password }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: { token?: string };
    error?: { message?: string };
  };
  if (!res.ok || !data.success || !data.data?.token) {
    throw new Error(data.error?.message || 'No se pudo iniciar sesión');
  }
  return data.data.token;
}

export type LandingStatsSummary = {
  pageViewsToday: number;
  pageViewsTotal: number;
  uniqueVisitors: number;
  demoRequestsToday: number;
  demoRequestsTotal: number;
  conversionRate: number;
  wizardOpens?: number;
};

export type LandingDailyStat = {
  date: string;
  pageViews: number;
  uniqueVisitors: number;
  demoRequests: number;
};

export type LandingNamedCount = {
  label: string;
  count: number;
};

export type LandingCountryStat = {
  countryIso: string | null;
  label: string;
  count: number;
};

export type LandingSectionStat = {
  section: string;
  views: number;
  uniqueSessions: number;
};

export type LandingStatsResponse = {
  periodDays: number;
  summary: LandingStatsSummary;
  daily: LandingDailyStat[];
  topReferrers: { referrer: string; count: number }[];
  topPaths?: LandingNamedCount[];
  topUtmSources?: LandingNamedCount[];
  topCountries?: LandingCountryStat[];
  topClickCountries?: LandingCountryStat[];
  topCtas?: LandingNamedCount[];
  topCtasByRegion?: LandingNamedCount[];
  topSections?: LandingSectionStat[];
  scrollDepth?: { percent: number; sessions: number }[];
  formFunnel?: { step: string; label: string; sessions: number }[];
};

export type LandingDemoRequestRow = {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string | null;
  message: string | null;
  emailSent: boolean;
  confirmationEmailSent: boolean;
  scheduledAt: string | null;
  meetUrl: string | null;
  status: string;
  createdAt: string;
};

async function adminFetch<T>(path: string, token: string): Promise<T> {
  const base = landingApiBase();
  if (!base) throw new Error('API no configurada.');
  const res = await fetch(`${base}${path}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: T;
    error?: { message?: string };
  };
  if (res.status === 401) {
    clearAdminToken();
    throw new Error('SESSION_EXPIRED');
  }
  if (!res.ok || !data.success) {
    throw new Error(data.error?.message || 'Error al cargar datos');
  }
  return data.data as T;
}

export function fetchLandingStats(token: string, days = 7): Promise<LandingStatsResponse> {
  return adminFetch(`/admin/stats?days=${days}`, token);
}

export function fetchDemoRequests(
  token: string,
  limit = 50,
  offset = 0
): Promise<{ rows: LandingDemoRequestRow[]; total: number; limit: number; offset: number }> {
  return adminFetch(`/admin/demo-requests?limit=${limit}&offset=${offset}`, token);
}
